import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';

import { mapPublicationFilters } from '@pressreader/catalog-common';
import {
    CatalogItemType,
    Category,
    CategoryGroupName,
    CategoryId,
    ICatalogLocale,
    ICatalogNavGroup,
    IPublicationFilters,
    SortableAttribute,
} from '@pressreader/catalog-types';
import { httpGet } from '@pressreader/services';

import { mapCatalogGroup, mapCatalogGroupWithParent, mapCatalogLocale } from './navgroups.mapper';
import { AnyCatalogNavPathItemDto, ICatalogGroupDto, ICatalogLocaleDto, ICatalogNavGroupsQuery, INavGroupsQueryRequest } from './types';

const SERVICE_PATH = 'catalog/v2';

async function getNavigationGroups(
    rootGroupNames: CategoryGroupName[] | string[],
    path?: string | number[],
    filters?: IPublicationFilters,
    orderBy?: SortableAttribute,
): Promise<ICatalogGroupDto[]> {
    let request: INavGroupsQueryRequest = {
        root: rootGroupNames,
    };

    if (path) {
        if (Array.isArray(path)) {
            request.path = path;
        } else {
            request.pathUri = path;
        }
    }

    if (orderBy) {
        request.orderBy = orderBy;
    }

    const filtersRequest = mapPublicationFilters(filters);

    request = { ...request, ...filtersRequest };

    return await httpGet<ICatalogGroupDto[]>(SERVICE_PATH, 'navigationgroups', request);
}

function getLocales(): Promise<ICatalogLocaleDto> {
    return httpGet(SERVICE_PATH, 'locales');
}

function loadFullNavGroupTree(rootGroupNames: CategoryGroupName[] | string[]): Promise<ICatalogGroupDto[]> {
    return httpGet(SERVICE_PATH, 'navigationtree', { root: rootGroupNames });
}

function getCategoryGroup(name: string): Promise<ICatalogGroupDto> {
    return httpGet(SERVICE_PATH, `categorygroups/${name}`);
}

function resolveNavigationPath(pathUri: string, prefer?: CatalogItemType): Promise<(AnyCatalogNavPathItemDto | null)[]> {
    return httpGet(SERVICE_PATH, 'navigationpath', { pathUri, prefer });
}

function getNavigationGroupsByQuery(query: ICatalogNavGroupsQuery) {
    return getNavigationGroups(query.rootGroupNames, query.path, query.filters, query.orderBy);
}

async function loadRootNavGroups(groupNames: string[], filters?: IPublicationFilters, orderBy?: SortableAttribute) {
    const navGroupsDtos = await getNavigationGroups(groupNames, [], filters, orderBy);
    return navGroupsDtos.map(mapCatalogGroup);
}

async function loadActiveCatalogNavGroups(groupNames: string[], dto: IPublicationFilters): Promise<ICatalogNavGroup[]> {
    const inCategories: Array<CategoryId[]> = (dto.inCategories as []).map(c => (Array.isArray(c) ? c : [c]));

    const catalogGroups = await loadRootNavGroups(groupNames, dto);

    if (inCategories.length === 0) {
        return catalogGroups;
    }

    const indexedCatalogGroups = keyBy(catalogGroups, g => g.id);
    const tasks = inCategories.map((catGroup, idx) => {
        const filteredDto = { ...dto, inCategories: inCategories.filter((g, pos) => pos !== idx) };
        return loadRootNavGroups(groupNames, filteredDto);
    });
    const groupsSet = await Promise.all(tasks);

    groupsSet.forEach((groups, idx) => {
        const activeGroup = groups.find(g => g.items.some(item => inCategories[idx].indexOf(item.id) >= 0));
        if (activeGroup) {
            indexedCatalogGroups[activeGroup.id] = activeGroup;
        }
    });
    return catalogGroups.map(g => indexedCatalogGroups[g.id]);
}

async function loadCatalogNavGroupsAsFilters(
    rootNavGroups: ICatalogNavGroup[],
    rootNavGroupsQuery: ICatalogNavGroupsQuery,
    selectedNavGroupItemIds: number[],
    // Some fan-out queries repeat ones the caller already ran (a lone selected group's
    // "all other groups" query IS the root query) — the caller decides how those fetch
    // (e.g. a per-query cache), so no default: an uncached fan-out must be chosen, not fallen into.
    fetchNavGroups: (query: ICatalogNavGroupsQuery) => ReturnType<typeof getNavigationGroupsByQuery>,
): Promise<ICatalogNavGroup[]> {
    // Showing regions filter supported when only one country is selected.
    const selectedCountryNavGroupItemIds = selectedNavGroupItemIds.filter(id =>
        rootNavGroups.some(g => g.id === Category.Countries && g.items.some(i => i.id === id)),
    );
    const selectedCountryId = selectedCountryNavGroupItemIds.length === 1 ? selectedCountryNavGroupItemIds[0] : null;

    let regionsNavGroupId: number | null = null;
    let selectedRegionId: number | null = null;
    let citiesNavGroupId: number | null = null;
    if (selectedCountryId) {
        const dtos = await fetchNavGroups({
            ...rootNavGroupsQuery,
            rootGroupNames: [CategoryGroupName.Countries],
            path: [...(rootNavGroupsQuery.path as number[]), selectedCountryId],
        });
        if (dtos?.length) {
            const regionsNavGroup = mapCatalogGroupWithParent(dtos[0], selectedCountryId);
            // Skip single-region case: filtering by the only region of the selected country is a no-op.
            if (regionsNavGroup.items.length > 1) {
                regionsNavGroupId = regionsNavGroup.id;
                selectedRegionId = selectedNavGroupItemIds.find(id => regionsNavGroup.items.some(item => item.id === id)) ?? null;
                rootNavGroups = [...rootNavGroups, regionsNavGroup];
            }
        }

        if (selectedRegionId) {
            const dtos = await fetchNavGroups({
                ...rootNavGroupsQuery,
                rootGroupNames: [CategoryGroupName.Countries],
                path: [...(rootNavGroupsQuery.path as number[]), selectedCountryId, selectedRegionId],
            });
            if (dtos?.length) {
                const citiesNavGroup = mapCatalogGroupWithParent(dtos[0], selectedRegionId);
                if (citiesNavGroup.items.length > 0) {
                    citiesNavGroupId = citiesNavGroup.id;
                    rootNavGroups = [...rootNavGroups, citiesNavGroup];
                }
            }
        }
    }

    // group selected items by groupId, filter out empty groupId (means that these groups not exist in rootNavGroups)
    const selectedByGroupId = selectedNavGroupItemIds.reduce<{ groupId: ICatalogNavGroup['id']; items: number[] }[]>((acc, itemId) => {
        const navGroup = rootNavGroups.find(g => g.items.some(i => i.id === itemId));
        if (!navGroup) {
            return acc;
        }

        let group = acc.find(g => g.groupId === navGroup.id);
        if (!group) {
            group = { groupId: navGroup.id, items: [] };
            acc.push(group);
        }

        group.items.push(itemId);

        return acc;
    }, []);

    // prepare query
    const query: ICatalogNavGroupsQuery = {
        ...rootNavGroupsQuery,
        filters: { ...rootNavGroupsQuery.filters },
    };

    // make sure inCategories in multiple array
    if ((query.filters.inCategories?.length ?? 0) > 0) {
        if (!Array.isArray(query.filters.inCategories?.[0])) {
            query.filters.inCategories = [query.filters.inCategories as CategoryId[]];
        }
    }
    const baseInCategories = (query.filters.inCategories ?? []) as CategoryId[][];

    const tasks: Promise<ICatalogNavGroup[]>[] = [];

    if (!selectedByGroupId.length) {
        tasks.push(Promise.resolve(cloneDeep(rootNavGroups)));
    } else {
        // Load filtered navgroups.
        const customQuery: ICatalogNavGroupsQuery = {
            ...query,
            filters: {
                ...query.filters,
                inCategories: [...baseInCategories, ...selectedByGroupId.map(g => g.items)],
            },
        };

        const filteredNavGroupsTask = fetchNavGroups(customQuery).then(result => result.map(mapCatalogGroup));

        if (!selectedCountryId) {
            tasks.push(filteredNavGroupsTask);
        } else {
            // Load regions as well.
            const filteredRegionsTask = fetchNavGroups({
                ...customQuery,
                rootGroupNames: [CategoryGroupName.Countries],
                path: [...(customQuery.path as number[]), selectedCountryId],
            }).then(dtos => (dtos?.length ? mapCatalogGroupWithParent(dtos[0], selectedCountryId) : null));
            const filteredCitiesTask =
                selectedRegionId !== null
                    ? fetchNavGroups({
                          ...customQuery,
                          rootGroupNames: [CategoryGroupName.Countries],
                          path: [...(customQuery.path as number[]), selectedCountryId, selectedRegionId],
                      }).then(dtos => (dtos?.length ? mapCatalogGroupWithParent(dtos[0], selectedRegionId) : null))
                    : Promise.resolve(null);

            tasks.push(
                Promise.all([filteredNavGroupsTask, filteredRegionsTask, filteredCitiesTask]).then(([navGroups, regionsNavGroup, citiesNavGroup]) => {
                    let insertIndex = navGroups.findIndex(g => g.id === Category.Countries);

                    if (regionsNavGroup) {
                        if (insertIndex >= 0) {
                            navGroups.splice(insertIndex + 1, 0, regionsNavGroup);
                            insertIndex++;
                        }
                    }
                    if (citiesNavGroup) {
                        if (insertIndex >= 0) {
                            navGroups.splice(insertIndex + 1, 0, citiesNavGroup);
                        }
                    }
                    return navGroups;
                }),
            );
        }
    }

    tasks.push(
        ...selectedByGroupId.map(async group => {
            const customQuery: ICatalogNavGroupsQuery = {
                ...query,
                filters: {
                    ...query.filters,
                    inCategories: [...baseInCategories, ...selectedByGroupId.filter(otherGroup => otherGroup !== group).map(g => g.items)],
                },
            };
            let mapper = mapCatalogGroup;

            if (group.groupId === regionsNavGroupId && selectedCountryId !== null) {
                customQuery.rootGroupNames = [CategoryGroupName.Countries];
                customQuery.path = [...(customQuery.path as number[]), selectedCountryId];
                mapper = (dto: ICatalogGroupDto) => mapCatalogGroupWithParent(dto, selectedCountryId);
            }
            if (group.groupId === citiesNavGroupId && selectedCountryId !== null && selectedRegionId !== null) {
                customQuery.rootGroupNames = [CategoryGroupName.Countries];
                customQuery.path = [...(customQuery.path as number[]), selectedCountryId, selectedRegionId];
                mapper = (dto: ICatalogGroupDto) => mapCatalogGroupWithParent(dto, selectedRegionId);
            }

            const groupDtos = await fetchNavGroups(customQuery);
            return groupDtos.map(mapper);
        }),
    );

    const newNavGroups = await Promise.all(tasks);

    const resultNavGroup = newNavGroups[0];
    newNavGroups.splice(0, 1);

    selectedByGroupId.forEach((group, idx) => {
        const newNavGroup = newNavGroups[idx].find(g => g.id === group.groupId);
        if (newNavGroup) {
            if (group.groupId === Category.Countries && selectedCountryId && selectedRegionId) {
                // Leave only countries that are allowed by selected region.
                newNavGroup.items = newNavGroup.items.filter(item => item.id === selectedCountryId);
            }

            group.items.forEach(groupitemId => {
                // if this item is not present in newNavGroup, add it from rootNavGroups with PublicationCount = 0

                if (!newNavGroup.items.find(i => i.id === groupitemId)) {
                    const missingItem = rootNavGroups.find(g => g.id === group.groupId)?.items.find(i => i.id === groupitemId);
                    if (missingItem) {
                        newNavGroup.items.push({ ...missingItem, publicationsCount: 0 });
                    }
                }
            });

            const replaceIndex = resultNavGroup.findIndex(g => g.id === group.groupId);
            if (replaceIndex >= 0) {
                resultNavGroup.splice(replaceIndex, 1, newNavGroup);
            } else {
                resultNavGroup.push(newNavGroup);
            }
        }
    });

    return resultNavGroup;
}

async function loadLocales(): Promise<ICatalogLocale[]> {
    const dto = await getLocales();
    return mapCatalogLocale(dto) ?? [];
}

export {
    getCategoryGroup,
    getLocales,
    getNavigationGroups,
    getNavigationGroupsByQuery,
    loadActiveCatalogNavGroups,
    loadCatalogNavGroupsAsFilters,
    loadFullNavGroupTree,
    loadLocales,
    loadRootNavGroups,
    resolveNavigationPath,
};
